import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import AnimatedFlightCard, { AddFlightButton, type EditableSegment } from './AnimatedFlightCard';
import type { FlightSegment } from './useBookingExtraction';
import type { IncidentType } from './types';

interface AnimatedFlightListProps {
  segments: EditableSegment[];
  mode: 'full' | 'compact';
  affectedSegmentId?: string | null;
  incidentType?: IncidentType | null;
  onUpdateSegment?: (id: string, field: keyof FlightSegment, value: string) => void;
  onDeleteSegment?: (id: string) => void;
  onAddSegment?: () => void;
  onSegmentClick?: (id: string) => void;
}

export default function AnimatedFlightList({
  segments,
  mode,
  affectedSegmentId,
  incidentType,
  onUpdateSegment,
  onDeleteSegment,
  onAddSegment,
  onSegmentClick,
}: AnimatedFlightListProps) {
  // Filter out deleted segments
  const visibleSegments = segments.filter((s) => !s._isDeleted);
  const isCompact = mode === 'compact';

  if (visibleSegments.length === 0 && !isCompact) {
    return (
      <div className="w-full max-w-md mx-auto">
        <AddFlightButton onClick={onAddSegment!} />
      </div>
    );
  }

  if (visibleSegments.length === 0) {
    return null;
  }

  return (
    <LayoutGroup>
      {/* Container - no initial animation to prevent re-animation on mode change */}
      <div className="w-full max-w-md mx-auto relative">
        {/* Timeline line - only in compact mode, animates in */}
        <AnimatePresence>
          {isCompact && (
            <motion.div
              key="timeline-line"
              className="absolute left-4 top-6 bottom-6 w-0.5 bg-slate-200"
              initial={{ scaleY: 0, originY: 0 }}
              animate={{ scaleY: 1 }}
              exit={{ scaleY: 0, originY: 0 }}
              transition={{ duration: 0.4 }}
            />
          )}
        </AnimatePresence>

        {/* Flight cards - consistent structure for both modes */}
        <motion.div
          layout
          className={isCompact ? 'space-y-3' : 'flex flex-col gap-3'}
        >
          {visibleSegments.map((segment, index) => {
            const isAffected = segment._id === affectedSegmentId;

            return (
              <AnimatedFlightCard
                key={segment._id}
                segment={segment}
                index={index}
                mode={mode}
                isAffected={isAffected}
                incidentType={incidentType}
                onUpdate={onUpdateSegment}
                onDelete={onDeleteSegment}
                onClick={onSegmentClick}
                showTimelineNode={isCompact}
              />
            );
          })}
        </motion.div>

        {/* Add flight button - only in full mode */}
        {!isCompact && onAddSegment && <AddFlightButton onClick={onAddSegment} />}
      </div>
    </LayoutGroup>
  );
}
